/**
 * link renderer（FR-LINK-06・EP-2）。誌面のリンク領域を描く。
 *
 * payload は 3 形態（サーバ側 `Overlay\OverlayInput` と対）:
 *   {page}        … 同じブックの別ページ（PDF の内部注釈・後付けの両方がこの形）
 *   {url,target}  … 外部 URL
 *   {book,page}   … 別ブックの特定ページ（カタログ間リンク）
 * 任意の `label` は読み上げ・ツールチップに使う（無ければ遷移先から文言を組む）。
 *
 * 外部・別ブックは `<a>`（別タブなら `rel="noopener noreferrer"` — NFR-SEC-02）、
 * 同一ブック内は `<button>` にする。ページ移動はビューワ内部の遷移であり、
 * `<a href="#page=…">` にするとハッシュナビ無効時に何も起きない URL になるため。
 *
 * 遷移先を解決できない payload では**何も描かない**（空の当たり判定を誌面に置かない）。
 */
import type { OverlayContext, OverlayRenderer } from '../registry';

interface LinkPayload {
  page?: number;
  url?: string;
  book?: string;
  target?: string;
  label?: string;
}

export const linkRenderer: OverlayRenderer = (el, item, context) => {
  const payload = item.payload as LinkPayload;
  const control = build(el.ownerDocument, payload, context);
  if (!control) return;

  const label = payload.label ?? describe(payload, context);
  control.setAttribute('aria-label', label);
  control.title = label;
  el.appendChild(control);
};

/** 遷移先に応じた要素を作る（解決できなければ null）。 */
function build(doc: Document, payload: LinkPayload, context: OverlayContext): HTMLElement | null {
  if (typeof payload.url === 'string') {
    return anchor(doc, payload.url, payload.target === '_self' ? '_self' : '_blank');
  }
  if (typeof payload.book === 'string' && typeof payload.page === 'number') {
    const url = context.bookUrl(payload.book, payload.page);
    return url === null ? null : anchor(doc, url, '_blank');
  }
  if (typeof payload.page === 'number') {
    const page = payload.page;
    const button = doc.createElement('button');
    button.type = 'button';
    button.addEventListener('click', () => {
      context.api.goto(page);
    });
    return button;
  }
  return null;
}

function anchor(doc: Document, href: string, target: string): HTMLAnchorElement {
  const a = doc.createElement('a');
  a.href = href;
  a.target = target;
  // 別タブは opener 経由の乗っ取りを防ぐ（NFR-SEC-02）。同タブでも referrer は渡さない。
  a.rel = target === '_blank' ? 'noopener noreferrer' : 'noreferrer';
  return a;
}

/** label 未指定時の読み上げ文言。 */
function describe(payload: LinkPayload, context: OverlayContext): string {
  if (typeof payload.url === 'string') {
    return context.translate('overlay.link.external', { url: payload.url });
  }
  if (typeof payload.book === 'string') {
    return context.translate('overlay.link.book', {
      book: payload.book,
      page: String(payload.page ?? ''),
    });
  }
  return context.translate('overlay.link.page', { page: String(payload.page ?? '') });
}
